import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { formatCurrency, CATEGORY_MAP } from '@tally/shared';
import { Check, X, Mail, ExternalLink, AlertTriangle } from 'lucide-react';

export function ReviewQueuePage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['reviews'],
    queryFn: () => api.get<{ success: true; data: any[] }>('/reviews'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.post(`/reviews/${id}/approve`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reviews'] }),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => api.post(`/reviews/${id}/dismiss`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reviews'] }),
  });

  const items = data?.data || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Review Queue</h1>
        <p className="text-sm text-muted-foreground">
          Emails that might be travel transactions but need your confirmation. Approve to add as a transaction, or dismiss.
        </p>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" />)}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-border py-16">
          <AlertTriangle className="mb-3 h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No items to review</p>
          <p className="text-xs text-muted-foreground/70">Uncertain emails will appear here for your approval</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item: any) => {
          const cat = CATEGORY_MAP.get(item.category);
          return (
            <div key={item.id} className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-800/30 dark:bg-amber-950/20">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">{formatCurrency(item.amount, item.currency)}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-xs">
                      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: cat?.color || '#94a3b8' }} />
                      {cat?.label || item.category}
                    </span>
                  </div>
                  <p className="mt-1 font-medium">{item.merchantName || 'Unknown'}</p>
                  <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                  <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{item.date?.split('T')[0]}</span>
                    {item.location && (
                      <span>{[item.location.city, item.location.country].filter(Boolean).join(', ')}</span>
                    )}
                    {item.sources?.[0]?.deepLink && (
                      <a href={item.sources[0].deepLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-0.5 text-primary hover:underline">
                        <Mail className="h-3 w-3" /> View email <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => approveMutation.mutate(item.id)}
                    disabled={approveMutation.isPending}
                    className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button
                    onClick={() => dismissMutation.mutate(item.id)}
                    disabled={dismissMutation.isPending}
                    className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" /> Dismiss
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
